/**
 * Interpreter keyword matrix — deterministic generator (no AI credits).
 *
 * Axes: scenario × language × city/venue × user intent. No prices, no
 * guarantees, no invented credentials.
 */
import { INTERPRETER_LANGUAGES, INTERPRETER_SCENARIOS } from "./index";

export const INTERPRETER_CITIES: readonly { slug: string; th: string }[] = [
  { slug: "bangkok", th: "กรุงเทพ" },
  { slug: "nonthaburi", th: "นนทบุรี" },
  { slug: "pathumthani", th: "ปทุมธานี" },
  { slug: "samutprakan", th: "สมุทรปราการ" },
  { slug: "chonburi", th: "ชลบุรี" },
  { slug: "pattaya", th: "พัทยา" },
  { slug: "rayong", th: "ระยอง" },
  { slug: "chiangmai", th: "เชียงใหม่" },
  { slug: "phuket", th: "ภูเก็ต" },
  { slug: "suratthani", th: "สุราษฎร์ธานี" },
  { slug: "khonkaen", th: "ขอนแก่น" },
  { slug: "udonthani", th: "อุดรธานี" },
  { slug: "nakhonratchasima", th: "นครราชสีมา" },
  { slug: "songkhla", th: "สงขลา" },
  { slug: "ayutthaya", th: "อยุธยา" },
] as const;

export const INTERPRETER_INTENTS: readonly { slug: string; th: string }[] = [
  { slug: "court-hearing", th: "ขึ้นศาล" },
  { slug: "police-statement", th: "ให้ปากคำตำรวจ" },
  { slug: "marriage-registration", th: "จดทะเบียนสมรส" },
  { slug: "divorce-registration", th: "จดทะเบียนหย่า" },
  { slug: "visa-interview", th: "สัมภาษณ์วีซ่า" },
  { slug: "immigration-office", th: "ติดต่อตรวจคนเข้าเมือง" },
  { slug: "poa-signing", th: "ลงนามหนังสือมอบอำนาจ" },
  { slug: "affidavit", th: "ทำคำให้การ (Affidavit)" },
  { slug: "property-transfer", th: "โอนอสังหาริมทรัพย์" },
  { slug: "board-meeting", th: "ประชุมกรรมการบริษัท" },
  { slug: "factory-audit", th: "ตรวจโรงงาน" },
  { slug: "hospital-visit", th: "พบแพทย์ที่โรงพยาบาล" },
  { slug: "insurance-claim", th: "เคลมประกัน" },
  { slug: "online-meeting", th: "ประชุมออนไลน์" },
] as const;

const PREFIXES = [
  "ล่าม",
  "จัดหาล่าม",
  "หาล่าม",
  "บริการล่าม",
  "ล่ามด่วน",
  "ล่ามมืออาชีพ",
  "interpreter",
  "interpreter service",
] as const;

function buildKeywords(): string[] {
  const out = new Set<string>();

  for (const l of INTERPRETER_LANGUAGES) {
    out.add(`ล่ามภาษา${l.th}`);
    out.add(`หาล่ามภาษา${l.th} ด่วน`);
    out.add(`ล่ามภาษา${l.th} ราชการ`);
    out.add(`${l.en.toLowerCase()} interpreter thailand`);
    out.add(`thai ${l.en.toLowerCase()} interpreter bangkok`);
    for (const p of PREFIXES) out.add(`${p} ${l.th}`);
    for (const s of INTERPRETER_SCENARIOS) {
      out.add(`${s.th} ภาษา${l.th}`);
      out.add(`ล่ามภาษา${l.th} ${s.venueTh}`);
    }
    for (const c of INTERPRETER_CITIES) {
      out.add(`ล่ามภาษา${l.th} ${c.th}`);
      out.add(`${l.en.toLowerCase()} interpreter ${c.slug}`);
    }
    for (const i of INTERPRETER_INTENTS) {
      out.add(`ล่ามภาษา${l.th} ${i.th}`);
    }
  }

  for (const s of INTERPRETER_SCENARIOS) {
    out.add(s.th);
    out.add(`${s.th} คืออะไร`);
    out.add(`${s.th} ต้องเตรียมอะไร`);
    out.add(`ขั้นตอน${s.th}`);
    out.add(`${s.en.toLowerCase()} thailand`);
    for (const c of INTERPRETER_CITIES) out.add(`${s.th} ${c.th}`);
    for (const i of INTERPRETER_INTENTS) out.add(`${s.th} เพื่อ${i.th}`);
  }

  for (const i of INTERPRETER_INTENTS) {
    out.add(`ต้องใช้ล่ามไหม ${i.th}`);
    out.add(`ล่าม${i.th} ต้องเตรียมเอกสารอะไร`);
  }

  for (const c of INTERPRETER_CITIES) {
    out.add(`ล่ามศาล ${c.th}`);
    out.add(`ล่ามอำเภอ ${c.th}`);
    out.add(`ล่ามโรงพยาบาล ${c.th}`);
    out.add(`interpreter near ${c.slug}`);
  }

  return [...out];
}

export const INTERPRETER_KEYWORDS: readonly string[] = buildKeywords();
export const INTERPRETER_KEYWORD_COUNT = INTERPRETER_KEYWORDS.length;
